import { RefreshCw } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import api from "../api/client";
import type { LeaveRecalculation } from "../types/api";
import { apiErrorMessage } from "../utils/apiError";
import { formatMinutes } from "../utils/format";
import { ErrorNotice, SuccessNotice } from "./Feedback";

/**
 * Vraagt na een wijziging van de ATV/verlofpot welke jaren opnieuw geboekt
 * moeten worden. Al goedgekeurd verlof volgt anders nog de oude regel.
 */
export default function LeaveRecalculationPrompt({
  query,
  reloadToken,
  canManage,
}: {
  query: string;
  reloadToken: number;
  canManage: boolean;
}) {
  const [data, setData] = useState<LeaveRecalculation | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [working, setWorking] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const load = useCallback(async () => {
    try {
      const suffix = query ? `?${query}` : "";
      const response = await api.get<LeaveRecalculation>(
        `/leave/recalculation/${suffix}`,
      );
      setData(response.data);
      setSelected(new Set(response.data.years.map((row) => row.year)));
    } catch {
      setData(null);
    }
  }, [query]);

  useEffect(() => {
    void load();
  }, [load, reloadToken]);

  if (!data || (!data.pool_enabled && data.years.length === 0)) return null;

  // Alleen goedgekeurd verlof is echt op een saldo geboekt. Staat er niets
  // goedgekeurd, dan valt er niets om te boeken; dat is beter om te zeggen dan
  // om het blok stil te laten verdwijnen.
  if (data.years.length === 0) {
    return (
      <section className="surface settings-section leave-recalculation">
        <div className="settings-heading">
          <h2>Bestaand verlof herberekenen</h2>
          <p>
            Er staat geen goedgekeurd verlof dat opnieuw geboekt hoeft te worden.
            Alleen goedgekeurd verlof is van een saldo afgeschreven; concepten en
            aanvragen volgen de nieuwe volgorde zodra ze worden goedgekeurd.
          </p>
        </div>
      </section>
    );
  }

  const recalculate = async () => {
    if (selected.size === 0) return;
    setWorking(true);
    setError("");
    setSuccess("");
    try {
      const suffix = query ? `?${query}` : "";
      const response = await api.post<{ entries: number; years: number[] }>(
        `/leave/recalculation/${suffix}`,
        { years: [...selected] },
      );
      setSuccess(
        `${response.data.entries} verlofregel(s) in ${response.data.years.join(
          ", ",
        )} zijn opnieuw geboekt.`,
      );
      await load();
    } catch (requestError: unknown) {
      setError(
        apiErrorMessage(requestError, "Het verlof kon niet worden herberekend."),
      );
    } finally {
      setWorking(false);
    }
  };

  return (
    <section className="surface settings-section leave-recalculation">
      {error && <ErrorNotice>{error}</ErrorNotice>}
      {success && <SuccessNotice>{success}</SuccessNotice>}
      <div className="settings-heading">
        <h2>Bestaand verlof herberekenen</h2>
        <p>
          Er staat al goedgekeurd verlof geboekt volgens de regels die golden op het
          moment van goedkeuren. Het totaal verandert niet, alleen de verdeling over ATV
          en verlof.
          {data.scope === "organization"
            ? " Dit geldt voor alle medewerkers in deze organisatie."
            : ""}
        </p>
      </div>
      <div className="policy-toggle-list">
        {data.years.map((row) => (
          <label className="toggle-field policy-toggle" key={row.year}>
            <input
              type="checkbox"
              checked={selected.has(row.year)}
              disabled={!canManage || working}
              onChange={(event) =>
                setSelected((current) => {
                  const next = new Set(current);
                  if (event.target.checked) next.add(row.year);
                  else next.delete(row.year);
                  return next;
                })
              }
            />
            <span>
              <strong>{row.year}</strong>
              <small>
                {row.entry_count} verlofregel(s) · {formatMinutes(row.minutes)}
              </small>
            </span>
          </label>
        ))}
      </div>
      {canManage && (
        <button
          className="button primary policy-save"
          type="button"
          disabled={working || selected.size === 0}
          onClick={() => void recalculate()}
        >
          <RefreshCw size={17} />{" "}
          {working ? "Herberekenen..." : "Geselecteerde jaren herberekenen"}
        </button>
      )}
    </section>
  );
}
